import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerResolvedQuantityFlags,
  getAICheckerKnownQuantityFlags,
  sendIssueToCodeMonkey,
  type FlaggedProductWithRestaurant,
} from '../../../../../../services/admin/adminService';
import MonkeyIcon from '../../../../../common/MonkeyIcon';
import { buildGroupedQuantityCaseInfo } from '../../shared/quantityPrompt';
import { QtyFlagTable } from './QtyFlagTable';
import { aiQtyFlagKey } from './aiQtyFlagKey';
import { groupFlagsByProduct, isAskCustomerFlag, qtyFlagAskState, type QtyFlagAskState } from './qtyFlagGrouping';
import type { MonkeyState, TFn } from '../../types';

// Flat, always-visible table of the quantity-info flags across emails in range, plus a persistent
// per-flag "mark done" and a "show done" toggle to review & undo accidental done clicks.
export function AICheckerQuantityFlags({ flags, account, startDate, endDate, onChanged, sourceFilter, t }: { sourceFilter?: { fileId: string; restaurantId?: string }; flags: FlaggedProductWithRestaurant[]; account: string; startDate: string; endDate: string; onChanged?: () => void; t: TFn }) {
  const [done, setDone] = useState<Set<string>>(new Set());
  const onDone = (key: string) => setDone((prev) => new Set(prev).add(key));

  // Lazily-loaded "done" flags (only fetched when the operator opens the section).
  const [showDone, setShowDone] = useState(false);
  const [doneFlags, setDoneFlags] = useState<FlaggedProductWithRestaurant[] | null>(null);
  const [reopened, setReopened] = useState<Set<string>>(new Set());
  const onReopen = (key: string) => setReopened((prev) => new Set(prev).add(key));

  // Lazily-loaded "known" flags (filed as to-dos) — so a mis-filed one can be undone back to open.
  const [showKnown, setShowKnown] = useState(false);
  const [knownFlags, setKnownFlags] = useState<FlaggedProductWithRestaurant[] | null>(null);
  const [unknown, setUnknown] = useState<Set<string>>(new Set());
  const onUnknown = (key: string) => setUnknown((prev) => new Set(prev).add(key));

  // Batch send: select several open flags (often variants of the same parsing gap) into ONE session.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (key: string) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [batchState, setBatchState] = useState<MonkeyState>('idle');

  const [reasonFilter, setReasonFilter] = useState<'all' | 'askCustomer' | 'notAskCustomer'>('all');
  const [askStateFilter, setAskStateFilter] = useState<QtyFlagAskState | null>(null);
  const toggleReasonFilter = (next: 'askCustomer' | 'notAskCustomer') => {
    setReasonFilter((current) => (current === next ? 'all' : next));
    setAskStateFilter(null);
  };
  const [uniqueOnly, setUniqueOnly] = useState(false);

  const loadDone = async () => {
    const next = !showDone;
    setShowDone(next);
    if (next && doneFlags === null) {
      try {
        setDoneFlags(await getAICheckerResolvedQuantityFlags(account, startDate, endDate));
      } catch (err) {
        console.error('Failed to load resolved quantity flags', err);
        setDoneFlags([]);
      }
    }
  };

  const loadKnown = async () => {
    const next = !showKnown;
    setShowKnown(next);
    if (next && knownFlags === null) {
      try {
        setKnownFlags(await getAICheckerKnownQuantityFlags(account, startDate, endDate));
      } catch (err) {
        console.error('Failed to load known quantity flags', err);
        setKnownFlags([]);
      }
    }
  };

  if (flags.length === 0) return null;
  const visible = flags.filter((f) => !done.has(aiQtyFlagKey(f)));
  const askFlags = visible.filter(isAskCustomerFlag);
  const askStateCount = (state: QtyFlagAskState) => askFlags.filter((f) => qtyFlagAskState(f) === state).length;
  const shown = reasonFilter === 'all'
    ? visible
    : visible.filter((f) => isAskCustomerFlag(f) === (reasonFilter === 'askCustomer') && (reasonFilter !== 'askCustomer' || !askStateFilter || qtyFlagAskState(f) === askStateFilter));
  const uniqueCount = groupFlagsByProduct(shown).size;
  const chipClass = (active: boolean) => cn('px-2.5 py-1 rounded-full text-xs border transition-colors',
    active
      ? 'border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700');
  const inSource = (flag: FlaggedProductWithRestaurant) => !sourceFilter || (flag.setupSource?.fileId === sourceFilter.fileId && (!sourceFilter.restaurantId || flag.setupSource.restaurantId === sourceFilter.restaurantId));
  const visibleDone = (doneFlags ?? []).filter((f) => inSource(f) && !reopened.has(aiQtyFlagKey(f)));
  const visibleKnown = (knownFlags ?? []).filter((f) => inSource(f) && !unknown.has(aiQtyFlagKey(f)));

  // Shared by the card-header button (everything selected) and the per-supplier header button
  // (that supplier's selection only). Deselects what it sent so the buttons disappear again.
  const sendSelection = async (toSend: FlaggedProductWithRestaurant[]) => {
    const caseInfo = `Multiple quantity-info patterns were flagged together. Investigate and fix EACH issue below — they are often variants of the same parsing gap, so look for one root cause first. Issues are grouped by the source file their order came from, and each group ends with that file's raw extracted text: use it to verify what the parser actually saw before deciding the correct quantityInfo and the code fix.\n\n`
      + await buildGroupedQuantityCaseInfo(toSend);
    // `platform` feeds the session label on the claude-manage dashboard; batches are almost
    // always within one supplier, but a cross-supplier selection lists them all.
    const platform = Array.from(new Set(toSend.map((f) => f.platform))).join(', ');
    await sendIssueToCodeMonkey({ flow: 'quantity-issue', caseInfo, context: { batch: true, count: toSend.length, platform } });
    const sentKeys = new Set(toSend.map(aiQtyFlagKey));
    setSelected((prev) => new Set(Array.from(prev).filter((k) => !sentKeys.has(k))));
  };

  const sendBatch = async () => {
    const toSend = visible.filter((f) => selected.has(aiQtyFlagKey(f)));
    if (toSend.length === 0 || batchState === 'loading') return;
    setBatchState('loading');
    try {
      await sendSelection(toSend);
      setBatchState('done');
      setTimeout(() => setBatchState('idle'), 2000);
    } catch (err) {
      console.error('Failed to batch-send quantity flags to code-monkey', err);
      setBatchState('error');
      setTimeout(() => setBatchState('idle'), 4000);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3 max-sm:flex-wrap max-sm:gap-2">
        <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>
          {t('adminTesting.review.quantityFlagsHeading', { count: shown.length })}
          <span className={cn(secondaryText, 'font-normal')}> · {t('adminTesting.review.quantityFlagsUnique', { count: uniqueCount })}</span>
        </h4>
        <div className="flex items-center gap-3 max-sm:flex-wrap">
          <button onClick={() => toggleReasonFilter('askCustomer')} aria-pressed={reasonFilter === 'askCustomer'} className={chipClass(reasonFilter === 'askCustomer')}>
            {t('adminTesting.review.quantityFlagsAskCustomerFilter', { count: askFlags.length })}
          </button>
          <button onClick={() => toggleReasonFilter('notAskCustomer')} aria-pressed={reasonFilter === 'notAskCustomer'} className={chipClass(reasonFilter === 'notAskCustomer')}>
            {t('adminTesting.review.quantityFlagsNotAskCustomerFilter', { count: visible.length - askFlags.length })}
          </button>
          <button onClick={() => setUniqueOnly((v) => !v)} aria-pressed={uniqueOnly} className={chipClass(uniqueOnly)}>
            {t('adminTesting.review.quantityFlagsUniqueToggle')}
          </button>
          {selected.size > 0 && (
            <button
              onClick={sendBatch}
              disabled={batchState === 'loading'}
              title={t('adminTesting.review.sendSelectedToTerminal', { count: selected.size })}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-white dark:text-white transition disabled:opacity-60',
                batchState === 'error' ? 'bg-red-600 dark:bg-red-600' : batchState === 'done' ? 'bg-green-600 dark:bg-green-600' : 'bg-backgroundDarker-dark dark:bg-backgroundDarker-dark hover:opacity-85',
              )}
            >
              <MonkeyIcon className={cn('w-3.5 h-3.5', batchState === 'loading' && 'animate-spin')} />
              {t('adminTesting.review.sendSelectedToTerminal', { count: selected.size })}
            </button>
          )}
          <button
            onClick={loadKnown}
            className={cn(secondaryText, 'text-xs underline hover:text-amber-600 dark:hover:text-amber-400')}
          >
            {showKnown ? t('adminTesting.review.hideKnown') : t('adminTesting.review.showKnown')}
          </button>
          <button
            onClick={loadDone}
            className={cn(secondaryText, 'text-xs underline hover:text-purple-600 dark:hover:text-purple-400')}
          >
            {showDone ? t('adminTesting.review.hideDone') : t('adminTesting.review.showDone')}
          </button>
        </div>
      </div>

      {reasonFilter === 'askCustomer' && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {([['toAsk', 'quantityFlagsAskStateToAsk'], ['pending', 'quantityFlagsAskStatePending'], ['approved', 'quantityFlagsAskStateApproved']] as const).map(([state, labelKey]) => (
            <button key={state} onClick={() => setAskStateFilter((current) => (current === state ? null : state))} aria-pressed={askStateFilter === state} className={chipClass(askStateFilter === state)}>
              {t(`adminTesting.review.${labelKey}`, { count: askStateCount(state) })}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>🎉 {t('adminTesting.review.allGoodDesc')}</div>
      ) : shown.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t(reasonFilter === 'notAskCustomer' ? 'adminTesting.review.quantityFlagsNoNotAskCustomer' : askStateFilter ? 'adminTesting.review.quantityFlagsNoAskState' : 'adminTesting.review.quantityFlagsNoAskCustomer')}</div>
      ) : (
        <QtyFlagTable flags={shown} uniqueByProduct={uniqueOnly} variant="open" onChanged={onDone} onMarkedKnown={onChanged} t={t} selected={selected} onToggleSelect={toggleSelect} onSendSelected={sendSelection} />
      )}

      {showKnown && (
        <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-900/40">
          <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-3')}>
            {t('adminTesting.review.knownFlagsHeading', { count: visibleKnown.length })}
          </h4>
          {knownFlags === null ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.review.aiCheckerLoading')}</div>
          ) : visibleKnown.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.review.noKnownFlags')}</div>
          ) : (
            <QtyFlagTable flags={visibleKnown} variant="known" onChanged={onUnknown} onMarkedKnown={onChanged} t={t} />
          )}
        </div>
      )}

      {showDone && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-3')}>
            {t('adminTesting.review.doneFlagsHeading', { count: visibleDone.length })}
          </h4>
          {doneFlags === null ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.review.aiCheckerLoading')}</div>
          ) : visibleDone.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.review.noDoneFlags')}</div>
          ) : (
            <QtyFlagTable flags={visibleDone} variant="done" onChanged={onReopen} t={t} />
          )}
        </div>
      )}
    </div>
  );
}
